import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { COUNTRIES } from '@app/data/_index';
import { SessionStorageKeys } from '@app/enums/_index';
import { HasAnalytics } from '@app/interfaces/_index';
import { RequestForm } from '@app/models/_index';
import { EmailService, GoogleAnalyticsService } from '@app/services/_index';
import { logGoogleAnalytics } from '@app/utilities/_index';

@Component({
  selector: 'app-request-form',
  templateUrl: './request-form.component.html',
  styleUrls: ['./request-form.component.scss'],
  standalone: false,
})
export class RequestFormComponent implements OnInit, HasAnalytics {
  submitted = false;
  requestComplete = false;
  requestDemoForm: FormGroup;
  firstname: string;
  lastname: string;
  email: string;
  countries: {};
  stateInfo: any[] = [];
  message: string;
  showError = false;
  requestedCourse: any;

  @Input() formHeading: string;

  constructor(
    private formBuilder: FormBuilder,
    private googleAnalyticsService: GoogleAnalyticsService,
    private emailService: EmailService
  ) {}

  /**
   * On Init
   * Load countries list and states
   * Build request demo form and set validators.
   */
  ngOnInit(): void {
    this.countries = COUNTRIES.Countries;
    this.stateInfo = COUNTRIES.Countries['230'].States;
    this.requestDemoForm = this.formBuilder.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern('[0-9]+')]],
      countryControl: ['230'],
      stateControl: ['0'],
      company: [''],
    });
  }

  /**
   * Use to return the Form controls. Used in template
   */
  get formControl(): any {
    return this.requestDemoForm.controls;
  }

  /**
   * Handles the Submit of the form
   * @returns error if the form is invalid
   */
  onRequestForm(): void {
    this.submitted = true;

    // stop here if form is invalid
    if (this.requestDemoForm.invalid) {
      return;
    }

    let emailDomain = 'Unknown';
    if (this.requestDemoForm && this.requestDemoForm.value && this.requestDemoForm.value.email) {
      emailDomain = this.requestDemoForm.value.email.split('@')[1];
    }

    const countryControl = this.requestDemoForm.value.countryControl;
    const stateControl = this.requestDemoForm.value.stateControl;

    const selectedCourse = sessionStorage.getItem(SessionStorageKeys.REQUEST_VOUCHER);
    if (selectedCourse) {
      try {
        const course = JSON.parse(selectedCourse);
        this.requestedCourse = [
          {
            courseId: course.id,
            name: course.name,
            code: course.code,
            courseType: course.type, // Usually 'eLearning'
            courseSelected: true,
            slug_name: course.slug,
          },
        ];
        // NOTE: Send the requested course as a single course in array.
        // This is in case we ever need to pass in a collection of courses.
        const requestFormData: RequestForm = {
          firstName: this.requestDemoForm.value.firstname,
          lastName: this.requestDemoForm.value.lastname,
          email: this.requestDemoForm.value.email,
          phone: this.requestDemoForm.value.phone,
          country: COUNTRIES.Countries[countryControl].CountryName,
          state: COUNTRIES.Countries[countryControl].States[stateControl].StateName,
          company: this.requestDemoForm.value.company,
          notes: 'This is a request form',
          courses: this.requestedCourse,
        };
        this.emailService
          .requestVoucher(requestFormData)
          .then(() => {
            this.sendGoogleAnalytics(this.formHeading, 'Submit', emailDomain);
            this.message =
              'Thank you for your request. A member of our team will be contacting you shortly to schedule a demo of Genesys Education.';
            this.requestComplete = true;
          })
          .catch((err) => {
            console.error('Error requesting form', err);
            this.showError = true;
          });
      } catch (JSONParseError) {
        console.log(
          `Error parsing the selectedCourse data: ${selectedCourse}. ERROR: ${JSONParseError}.`
        );
      }
    } else {
      alert(
        'Sorry, we had a problem processing your submission. Try again or contact us at beyond@genesys.com for assistance.'
      );
    }
  }

  /**
   * On change of a country select
   * @param countryValue - returns the country value
   */
  onChangeCountry(countryValue): void {
    this.stateInfo = this.countries[countryValue].States;
  }

  /**
   * Add Track By Country item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of country array
   * @param item - Country list item
   * @returns Country name
   */
  trackByCountryTitle(index: number, item: any): string {
    if (item) {
      return item.CountryName;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By State item to not redraw or
   * re-render list items that have not changed. This improves performance.
   * @param index - Index of State array
   * @param item - State list item
   * @returns State or Province name
   */
  trackByStateTitle(index: number, item: any): string {
    if (item) {
      return item.StateName;
    } else {
      return String(index);
    }
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }
}
